import { useCallback, useState } from 'react';
import { useForgotPassword } from './useForgotPassword';

type ModalStep = 'email' | 'code-password' | 'success';

interface UseForgotPasswordModalReturn {
  // Step management
  step: ModalStep;

  // Form state
  email: string;
  code: string;
  newPassword: string;
  confirmPassword: string;
  passwordValid: boolean;

  // Form handlers
  setEmail: (value: string) => void;
  setCode: (value: string) => void;
  setNewPassword: (value: string) => void;
  setConfirmPassword: (value: string) => void;
  setPasswordValid: (valid: boolean) => void;

  // Messages
  errorMessage: string;
  resendMessage: string;

  // Operations
  handleSendCode: () => Promise<void>;
  handleResendCode: () => Promise<void>;
  handleResetPassword: () => Promise<void>;

  // Modal lifecycle
  resetModal: () => void;

  // Status
  isProcessing: boolean;
}

/**
 * Hook to manage forgot password modal flow with multi-step process.
 * Platform-agnostic: Can be used in React Native.
 *
 * Manages:
 * - Multi-step flow (email -> code+password -> success)
 * - Form state for all input fields
 * - Error and success messages
 * - Integration with useForgotPassword for API calls
 *
 * @returns Forgot password modal state, handlers, and operations
 */
export function useForgotPasswordModal(): UseForgotPasswordModalReturn {
  const [step, setStep] = useState<ModalStep>('email');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordValid, setPasswordValid] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [resendMessage, setResendMessage] = useState('');

  const forgotPassword = useForgotPassword();

  const resetModal = useCallback(() => {
    setStep('email');
    setEmail('');
    setCode('');
    setNewPassword('');
    setConfirmPassword('');
    setPasswordValid(false);
    setErrorMessage('');
    setResendMessage('');
  }, []);

  const handleSendCode = useCallback(async () => {
    if (!email || !email.includes('@')) {
      setErrorMessage('Please enter a valid email address');
      return;
    }

    setErrorMessage('');
    const result = await forgotPassword.requestResetCode(email);

    if (result.success) {
      setStep('code-password');
    } else {
      setErrorMessage(result.error || 'Failed to send code. Please try again.');
    }
  }, [email, forgotPassword]);

  const handleResendCode = useCallback(async () => {
    setResendMessage('');
    setErrorMessage('');
    const result = await forgotPassword.requestResetCode(email);

    if (result.success) {
      setResendMessage('Code sent!');
    } else {
      setResendMessage('Failed to resend. Please try again.');
    }
  }, [email, forgotPassword]);

  const handleResetPassword = useCallback(async () => {
    if (!code || code.length !== 6) {
      setErrorMessage('Please enter the 6-digit code');
      return;
    }

    if (!passwordValid) {
      setErrorMessage('Please ensure your password meets all requirements');
      return;
    }

    setErrorMessage('');
    const result = await forgotPassword.confirmNewPassword(email, code, newPassword);

    if (result.success) {
      setStep('success');
    } else {
      setErrorMessage(result.error || 'Failed to reset password. Please try again.');
    }
  }, [code, passwordValid, email, newPassword, forgotPassword]);

  return {
    step,
    email,
    code,
    newPassword,
    confirmPassword,
    passwordValid,
    setEmail: useCallback((value: string) => {
      setEmail(value);
      setErrorMessage('');
    }, []),
    setCode: useCallback((value: string) => {
      setCode(value);
      setErrorMessage('');
    }, []),
    setNewPassword: useCallback((value: string) => {
      setNewPassword(value);
      setErrorMessage('');
    }, []),
    setConfirmPassword: useCallback((value: string) => {
      setConfirmPassword(value);
      setErrorMessage('');
    }, []),
    setPasswordValid,
    errorMessage,
    resendMessage,
    handleSendCode,
    handleResendCode,
    handleResetPassword,
    resetModal,
    isProcessing: forgotPassword.isProcessing,
  };
}
